import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import ScrollToTop from './ScrollToTop';

import SignIn from '../pages/Auth/SignIn/SignIn';
import ForgotPassword from '../pages/Auth/ForgotPassword/ForgotPassword';
import SignUp from '../pages/Auth/SignUp/SignUp';
import GoogleCallback from '../pages/Auth/GoogleCallback/GoogleCallback';
import PublicProfile from '../pages/PublicProfile/PublicProfile';
import SharedCertificate from '../pages/SharedCertificate/SharedCertificate';
import CandleClash from '../pages/CandleClash/CandleClash';
import AlphaDna from '../pages/AlphaDna/AlphaDna';
import AlphaOps from '../pages/AlphaOps/AlphaOps';

import Dashboard from '../pages/Dashboard/Dashboard';
import AccountMetrics from '../pages/AccountMetrics/AccountMetrics';
import TradeHistory from '../pages/TradeHistory/TradeHistory';
import PaymentHistory from '../pages/PaymentHistory/PaymentHistory';
import VerificationLayout from '../pages/Verification/VerificationLayout';
import PaymentVerification from '../pages/Verification/Payment/PaymentVerification';
import IdentityVerification from '../pages/Verification/Identity/IdentityVerification';
import KycVerification from '../pages/Verification/Kyc/KycVerification';
// Document Verification is hidden for now — see its route below.
// import DocumentVerification from '../pages/Verification/Documents/DocumentVerification';
import Contracts from '../pages/Verification/Contracts/Contracts';
import AccountSetup from '../pages/AccountSetup/AccountSetup';
import Downloads from '../pages/Downloads/Downloads';
import Tutorials from '../pages/Tutorials/Tutorials';
import HelpCentre from '../pages/HelpCentre/HelpCentre';
import Notifications from '../pages/Notifications/Notifications';
import Divento from '../pages/Divento/Divento';
import AlphaPulse from '../pages/AlphaPulse/AlphaPulse';
import BreachAnalysis from '../pages/BreachAnalysis/BreachAnalysis';

import PayoutLayout from '../pages/PayoutCertificates/PayoutLayout';
import Payout from '../pages/PayoutCertificates/Payout/Payout';
import Certificates from '../pages/PayoutCertificates/Certificates/Certificates';
import RequestPayout from '../pages/PayoutCertificates/RequestPayout/RequestPayout';

import CompetitionOverview from '../pages/Competition/Overview/Overview';
import Competitions from '../pages/Competition/Competitions/Competitions';
import CompetitionDetail from '../pages/Competition/CompetitionDetail/CompetitionDetail';

import Coupons from '../pages/Coupons/Coupons';
import CTrader from '../pages/CTrader/CTrader';
import NotFound from '../pages/NotFound/NotFound';
import Trademate from '../pages/Trademate/Trademate';

import AffiliateLayout from '../pages/Affiliate/AffiliateLayout';
// Only Affiliate Marketing shows for now — the Dashboard (Partner Programme)
// and the pages below are to be enabled later, with their routes.
// import PartnerProgramme from '../pages/Affiliate/PartnerProgramme/PartnerProgramme';
// Hidden for now — to be enabled later, with their routes below.
// import Giveaway from '../pages/Affiliate/Giveaway/Giveaway';
// import WinnerSubmission from '../pages/Affiliate/WinnerSubmission/WinnerSubmission';
// import PersonalAccount from '../pages/Affiliate/PersonalAccount/PersonalAccount';
// import Retainer from '../pages/Affiliate/Retainer/Retainer';
import AffiliateMarketing from '../pages/Affiliate/Marketing/AffiliateMarketing';
import AffiliateMarketingOverview from '../pages/Affiliate/Marketing/Overview/Overview';
import AffiliateCodes from '../pages/Affiliate/Marketing/AffiliateCodes/AffiliateCodes';
import CommissionPayouts from '../pages/Affiliate/Marketing/CommissionPayouts/CommissionPayouts';

import ProfileLayout from '../pages/Profile/ProfileLayout';
import ProfilePersonal from '../pages/Profile/Personal/Personal';
import ProfilePassword from '../pages/Profile/Password/Password';
import ProfileTradeLocker from '../pages/Profile/TradeLocker/TradeLocker';
import ProfileDxTrade from '../pages/Profile/DxTrade/DxTrade';
import ProfileSecurity from '../pages/Profile/Security/Security';

import TradingAssessment from '../pages/TradingAssessment/TradingAssessment';
import MyAlpha from '../pages/MyAlpha/MyAlpha';

export default function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Signed-out only — an authenticated visitor is sent to the dashboard. */}
        <Route element={<PublicRoute />}>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/sign-up" element={<SignUp />} />
        </Route>

        {/* Genuinely public: a trader's shared profile is readable by anyone,
            signed in or not, so it sits outside both guards. */}
        <Route path="/p/:accountId" element={<PublicProfile />} />

        {/* Marketing game: players identify by nickname + email rather than an
            account, so it sits outside both guards and the app shell. */}
        <Route path="/candle-clash" element={<CandleClash />} />

        {/* "Coming soon" teasers: full-screen 3D scenes with a waitlist form,
            outside the shell. Public, like Candle Clash — they show no account
            data. Their "Dashboard" button leads back in. */}
        <Route path="/alpha-dna" element={<AlphaDna />} />
        <Route path="/alpha-ops" element={<AlphaOps />} />

        {/* Same deal for a shared certificate — the path is what the share
            dialog hands out, so it can't move without invalidating links
            already in the wild. */}
        <Route path="/share-certificates/:certificateId" element={<SharedCertificate />} />

        {/* Google's OAuth redirect target — the path is registered with Google,
            so it can't move. Outside both guards on purpose: it runs with no
            session yet, and PublicRoute would redirect to the dashboard the
            moment the exchange lands, losing the `next` the trader came for. */}
        <Route path="/auth/google" element={<GoogleCallback />} />

        {/* Everything below needs a session. The guard wraps AppLayout rather
            than each page, so a new route is protected by default. */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="account-metrics" element={<AccountMetrics />} />
            <Route path="account-analysis" element={<Navigate to="/account-metrics?tab=advanced" replace />} />
            <Route path="trade-history" element={<TradeHistory />} />
            <Route path="payment-history" element={<PaymentHistory />} />
            {/* Payment & Verification — the layout holds the section's state
                and hangs the section aside on the shell, as Affiliate does. */}
            <Route path="verification" element={<VerificationLayout />}>
              <Route index element={<PaymentVerification />} />
              {/* Verification: KYC (Veriff) and Document tabs, one route each. */}
              <Route path="identity" element={<IdentityVerification />}>
                <Route index element={<KycVerification />} />
                {/* Document Verification, hidden for now (not needed yet). An old
                    link lands on the KYC tab instead of a 404. To bring it back,
                    restore this route, its import above, its tab in
                    IdentityVerification.jsx and its crumb in breadcrumbs.js. */}
                {/* <Route path="documents" element={<DocumentVerification />} /> */}
                <Route path="documents" element={<Navigate to="/verification/identity" replace />} />
              </Route>
              <Route path="contracts" element={<Contracts />} />
            </Route>
            {/* Where DigiSigner sends the trader once a contract is signed (the
                old dashboard's return address) — back to the list, which reads
                fresh on the way in. */}
            <Route path="doc/signing_completed" element={<Navigate to="/verification/contracts" replace />} />
            <Route path="account-setup" element={<AccountSetup />} />
            <Route path="downloads" element={<Downloads />} />
            <Route path="tutorials" element={<Tutorials />} />
            <Route path="help-centre" element={<HelpCentre />} />
            {/* Reached from the bell and the account menus rather than the
                sidebar — it is a place you go when something arrived, not a
                section of the product. */}
            <Route path="notifications" element={<Notifications />} />
            <Route path="divento" element={<Divento />} />
            <Route path="alpha-pulse" element={<AlphaPulse />} />

            {/* The layout route exists only to hang the phone's section
                picker on the shell — the sidebar's sub-nav covers every other
                width. */}
            <Route path="payout" element={<PayoutLayout />}>
              <Route index element={<Payout />} />
              {/* `?type=trading|affiliate` — which tab's form to draw. */}
              <Route path="request" element={<RequestPayout />} />
              <Route path="certificates" element={<Certificates />} />
            </Route>

            <Route path="competition">
              <Route index element={<CompetitionOverview />} />
              <Route path="all" element={<Competitions />} />
              <Route path=":competitionId" element={<CompetitionDetail />} />
            </Route>

            <Route path="coupons" element={<Coupons />} />
            {/* cTrader Web (N80) — the nav row is for cTrader clients only. */}
            <Route path="ctrader" element={<CTrader />} />
            {/* Paths match case-insensitively, so live's `/Trademate` lands here too. */}
            <Route path="trademate" element={<Trademate />} />

          {/* The section aside (1:16694) is hung on the shell by the layout
              route, which is also why `/affiliate/*` runs on the icon rail. */}
          <Route path="affiliate" element={<AffiliateLayout />}>
            {/* Hidden for now — to be enabled later. Until then the section and
                any old link open Affiliate Marketing rather than a 404. */}
            {/* <Route index element={<PartnerProgramme />} /> */}
            <Route index element={<Navigate to="/affiliate/marketing" replace />} />
            {/* <Route path="giveaway" element={<Giveaway />} /> */}
            {/* <Route path="winner-submission" element={<WinnerSubmission />} /> */}
            {/* <Route path="personal-account" element={<PersonalAccount />} /> */}
            {/* <Route path="retainer" element={<Retainer />} /> */}
            <Route path="giveaway" element={<Navigate to="/affiliate/marketing" replace />} />
            <Route path="winner-submission" element={<Navigate to="/affiliate/marketing" replace />} />
            <Route path="personal-account" element={<Navigate to="/affiliate/marketing" replace />} />
            <Route path="retainer" element={<Navigate to="/affiliate/marketing" replace />} />
            {/* Live's Affiliate Marketing tabs, each its own route. */}
            <Route path="marketing" element={<AffiliateMarketing />}>
              <Route index element={<AffiliateMarketingOverview />} />
              <Route path="codes" element={<AffiliateCodes />} />
              <Route path="payouts" element={<CommissionPayouts />} />
            </Route>
          </Route>

            {/* Profile draws its section nav inside the page rather than on the
                shell (unlike Affiliate), so this layout route exists only to
                keep that nav mounted across the five sections. */}
            <Route path="profile" element={<ProfileLayout />}>
              <Route index element={<ProfilePersonal />} />
              <Route path="password" element={<ProfilePassword />} />
              <Route path="tradelocker" element={<ProfileTradeLocker />} />
              <Route path="dxtrade" element={<ProfileDxTrade />} />
              {/* Labelled "2FA" in the nav; the path stays `security`, which is
                  what the sidebar account menu already links. */}
              <Route path="security" element={<ProfileSecurity />} />
            </Route>

            {/* ponytail: this uses the 76px icon-rail shell in Figma, not the 220px sidebar.
                Routed through AppLayout until Sidebar takes a `rail` variant. */}
            <Route path="trading-assessment" element={<TradingAssessment />} />

            {/* The MyAlpha landing page, full-bleed beside the sidebar
                (FLUSH_ROUTES in AppLayout). */}
            <Route path="my-alpha" element={<MyAlpha />} />

            {/* Breach Analysis stays out of the sidebar and the dock per the Style Guide's
                product rules — the only way in is Account Metrics' "View all analytics". */}
            <Route path="breach-analysis" element={<BreachAnalysis />} />

            {/* An unknown address says so (N70) rather than quietly landing on
                the dashboard. */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
